import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'age', 'sex', 'exercise', 'diet', 'smoking', 'alcohol', 'sleep', 'stress',
    'bmi', 'family', 'social',
    'lifeExpectancy', 'status', 'statusBadge', 'resultContainer',
    'healthScore', 'healthScoreBar',
    'exerciseContrib', 'dietContrib', 'smokingContrib', 'alcoholContrib',
    'sleepContrib', 'stressContrib', 'bmiContrib', 'familyContrib', 'socialContrib',
    'potentialYears', 'insight'
  ]

  // Base life expectancy by sex (US averages)
  baseLifeExpectancy = {
    male: 76,
    female: 81
  }

  connect() {
    this.calculate()
  }

  calculate() {
    const _age = parseInt(this.ageTarget.value, 10) || 35
    const sex = this.sexTargets.find((r) => r.checked)?.value || 'male'
    const exercise = parseInt(this.exerciseTarget.value, 10)
    const diet = parseInt(this.dietTarget.value, 10)
    const smoking = parseInt(this.smokingTarget.value, 10)
    const alcohol = parseInt(this.alcoholTarget.value, 10)
    const sleep = parseInt(this.sleepTarget.value, 10)
    const stress = parseInt(this.stressTarget.value, 10)
    const bmi = parseInt(this.bmiTarget.value, 10)
    const family = parseInt(this.familyTarget.value, 10)
    const social = parseInt(this.socialTarget.value, 10)

    // Calculate contributions
    const contributions = this.calculateContributions(exercise, diet, smoking, alcohol, sleep, stress, bmi, family, social)

    // Calculate total adjustment
    const totalAdjustment = Object.values(contributions).reduce((sum, val) => sum + val, 0)

    // Calculate life expectancy
    const baseLE = this.baseLifeExpectancy[sex]
    const lifeExpectancy = Math.round(baseLE + totalAdjustment)

    // Calculate health score (0-100)
    const healthScore = this.calculateHealthScore(contributions)

    // Calculate potential improvement
    const potentialYears = this.calculatePotentialImprovement(contributions)

    // Update displays
    this.lifeExpectancyTarget.textContent = lifeExpectancy
    this.healthScoreTarget.textContent = healthScore
    this.healthScoreBarTarget.style.width = `${healthScore}%`
    this.potentialYearsTarget.textContent = `+${potentialYears}`

    // Update contribution displays
    this.updateContributionDisplays(contributions)

    // Update status
    this.updateStatus(lifeExpectancy, sex)

    // Update insight
    this.updateInsight(contributions)
  }

  calculateContributions(exercise, diet, smoking, alcohol, sleep, stress, bmi, family, social) {
    // Exercise contribution (-3 to +4 years)
    const exerciseContribs = { 0: -3, 1: 0, 2: 2, 3: 3.5, 4: 4 }

    // Diet contribution (-2.5 to +3 years)
    const dietContribs = { 0: -2.5, 1: 0, 2: 1.5, 3: 3 }

    // Smoking contribution (-8 to +0.5 years)
    const smokingContribs = { 0: 0.5, 1: -0.5, 2: -2, 3: -5, 4: -8 }

    // Alcohol contribution (-4 to +0.5 years)
    const alcoholContribs = { 0: 0.5, 1: 0, 2: -2, 3: -4 }

    // Sleep contribution (-3 to +2 years)
    const sleepContribs = { 0: -3, 1: -1, 2: 1, 3: 2 }

    // Stress contribution (-3 to +1 years)
    const stressContribs = { 0: 1, 1: 0, 2: -1.5, 3: -3 }

    // BMI contribution (-3 to +1.5 years)
    const bmiContribs = { 0: -0.5, 1: 1.5, 2: -1, 3: -3 }

    // Family history contribution (0 to +4 years)
    const familyContribs = { 0: -2, 1: 0, 2: 2, 3: 4 }

    // Social connections contribution (-2 to +3 years)
    const socialContribs = { 0: -2, 1: 0.5, 2: 3 }

    return {
      exercise: exerciseContribs[exercise] || 0,
      diet: dietContribs[diet] || 0,
      smoking: smokingContribs[smoking] || 0,
      alcohol: alcoholContribs[alcohol] || 0,
      sleep: sleepContribs[sleep] || 0,
      stress: stressContribs[stress] || 0,
      bmi: bmiContribs[bmi] || 0,
      family: familyContribs[family] || 0,
      social: socialContribs[social] || 0
    }
  }

  calculateHealthScore(contributions) {
    // Map total adjustment (-30 to +20 range) to 0-100 score
    const total = Object.values(contributions).reduce((sum, val) => sum + val, 0)
    // Normalize: -30 = 0, +20 = 100
    const normalized = ((total + 30) / 50) * 100
    return Math.round(Math.max(0, Math.min(100, normalized)))
  }

  calculatePotentialImprovement(contributions) {
    // Calculate how many years could be gained by optimizing negative factors
    let potential = 0
    const maxValues = {
      exercise: 4,
      diet: 3,
      smoking: 0.5,
      alcohol: 0.5,
      sleep: 2,
      stress: 1,
      bmi: 1.5,
      family: 4,
      social: 3
    }

    Object.entries(contributions).forEach(([key, value]) => {
      if (value < maxValues[key] && key !== 'family') {
        potential += maxValues[key] - value
      }
    })

    return Math.round(potential)
  }

  updateContributionDisplays(contributions) {
    const targets = {
      exercise: this.exerciseContribTarget,
      diet: this.dietContribTarget,
      smoking: this.smokingContribTarget,
      alcohol: this.alcoholContribTarget,
      sleep: this.sleepContribTarget,
      stress: this.stressContribTarget,
      bmi: this.bmiContribTarget,
      family: this.familyContribTarget,
      social: this.socialContribTarget
    }

    Object.entries(targets).forEach(([key, target]) => {
      const value = contributions[key]
      target.textContent = this.formatContribution(value)
      this.colorContribution(target, value)
    })
  }

  formatContribution(value) {
    if (value === 0) return '0'
    const sign = value > 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}`
  }

  colorContribution(element, value) {
    element.classList.remove('text-green-600', 'text-red-600', 'text-gray-600')
    if (value > 0) {
      element.classList.add('text-green-600')
    } else if (value < 0) {
      element.classList.add('text-red-600')
    } else {
      element.classList.add('text-gray-600')
    }
  }

  updateStatus(lifeExpectancy, sex) {
    const avgLE = this.baseLifeExpectancy[sex]
    const diff = lifeExpectancy - avgLE

    let status; let
      colorClass
    if (diff >= 5) {
      status = 'EXCELLENT'
      colorClass = 'border-green-500 text-green-700'
    } else if (diff >= 2) {
      status = 'ABOVE AVERAGE'
      colorClass = 'border-green-500 text-green-700'
    } else if (diff >= -2) {
      status = 'AVERAGE'
      colorClass = 'border-gray-400 text-gray-700'
    } else if (diff >= -5) {
      status = 'BELOW AVERAGE'
      colorClass = 'border-yellow-500 text-yellow-700'
    } else {
      status = 'NEEDS IMPROVEMENT'
      colorClass = 'border-red-500 text-red-700'
    }

    this.statusTarget.textContent = status
    this.statusBadgeTarget.className = `px-4 py-2 border-2 font-medium text-sm ${colorClass}`
  }

  updateInsight(contributions) {
    const factorNames = {
      exercise: 'exercise habits',
      diet: 'diet quality',
      smoking: 'smoking status',
      alcohol: 'alcohol consumption',
      sleep: 'sleep quality',
      stress: 'stress management',
      bmi: 'weight management',
      family: 'family history',
      social: 'social connections'
    }

    // Find best and worst modifiable factors (exclude family)
    const modifiable = Object.entries(contributions)
      .filter(([key]) => key !== 'family')

    const best = modifiable.reduce((a, b) => (a[1] > b[1] ? a : b))
    const worst = modifiable.reduce((a, b) => (a[1] < b[1] ? a : b))

    let insight
    if (worst[1] < 0) {
      insight = `Your ${factorNames[best[0]]} is your strongest longevity factor. ` +
        `Focus on improving ${factorNames[worst[0]]} for the biggest potential gains.`
    } else {
      insight = `Excellent! Your lifestyle choices are supporting longevity. ` +
        `Your ${factorNames[best[0]]} is particularly strong. Keep it up!`
    }

    this.insightTarget.textContent = insight
  }
}
